import { PaginatedResponse } from '../../models/PaginatedResponse.model'
import { Trail } from '../../models/Trail.model'
import { TrailDTO } from '../../models/TrailDTO.model'
import { TrailAddEvent } from '../../events/TrailAddEvent'
import { TrailUpdateEvent } from '../../events/TrailUpdateEvent'
import { TrailRelations } from '../../models/TrailRelations.model'
import { TrailSearchEvent } from '../../events/TrailSearchEvent'
import { TrailWay } from '../../models/TrailWay.model'

import FlowfeedApiQueryService, {
  FlowfeedApiQueryServiceGetEvent,
} from '../FlowfeedApiQueryService/FlowfeedApiQueryService'
import { WeatherGroupService } from '../WeatherGroupService/WeatherGroupService'
import { TrailSystemService } from '../TrailSystemService/TrailSystemService'
import { formatToNumberTypeProps } from '../../utils/formatting'
import { ManualTrailStatusService } from '../ManualTrailStatusService/ManualTrailStatusService'
import { TrailFeatureService } from '../TrailFeatureService/TrailFeatureService'
import { SponsorService } from '../SponsorService/SponsorService'
import { TrailStatusService } from '../TrailStatusService/TrailStatusService'
import { ConditionalStatusGroupService } from '../ConditionalStatusGroupService/ConditionalStatusGroupService'

export class TrailService {
  /**
   * @description Search for a single trail by id or permalink
   *
   * @param id
   * @param permalink
   * @param relations
   *
   * @returns promise for a Trail
   */
  public async get({
    id,
    permalink,
    relations,
  }: {
    id?: number
    permalink?: string
    relations?: TrailRelations[]
  }): Promise<Trail> {
    const params = {
      id,
      permalink,
      relations:
        Array.isArray(relations) && !relations.length
          ? 'false' // false is treated as an empty array by the API since an empty string would be treated as default relations normally
          : relations?.join(',') || undefined,
    }
    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: `trail/get`,
      params,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const trailRaw = await flowfeedApiQueryService.get<TrailDTO>(flowfeedApiQueryRequest)
      return TrailService.formatTrail(trailRaw)
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }

  public async search(event: TrailSearchEvent): Promise<PaginatedResponse<Trail>> {
    const params = {
      ...event,
      name: event.name || undefined,
      orderDirection: event.orderDirection?.toUpperCase(),
      trailFeatureIds: event.trailFeatureIds?.join(',') || undefined,
      ids: event.ids?.join(',') || undefined,
      systemIds: event.systemIds?.join(',') || undefined,
      relations:
        Array.isArray(event.relations) && !event.relations.length
          ? 'false' // false is treated as an empty array by the API since an empty string would be treated as default relations normally
          : event.relations?.join(',') || undefined,
    }

    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: 'trail',
      params,
    }

    const trails: {
      data: Trail[]
      total: number
    } = {
      data: [],
      total: 0,
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const rawTrails = await flowfeedApiQueryService.get<PaginatedResponse<TrailDTO>>(flowfeedApiQueryRequest)
      // wrap to avoid errors if the request is cancelled
      if (rawTrails?.data) {
        trails.data = rawTrails.data.map((trail) => TrailService.formatTrail(trail))
        trails.total = rawTrails.total
      }
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }

    return trails
  }

  public async add(trail: TrailAddEvent): Promise<Trail> {
    const flowfeedApiQueryRequest = {
      endpoint: 'trail',
      // Coerce DetailInput string values to numbers; the API validates these as numbers and
      // formatToNumberTypeProps leaves null/undefined untouched so inheritance is preserved.
      data: formatToNumberTypeProps(trail, ['saturationModifier', 'dryTimeModifier', 'precipitationLookbackDays']),
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const trailRaw = await flowfeedApiQueryService.post<TrailDTO>(flowfeedApiQueryRequest)
      return TrailService.formatTrail(trailRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async update(id: number, trail: TrailUpdateEvent): Promise<Trail> {
    const flowfeedApiQueryRequest = {
      endpoint: `trail/${id}`,
      data: formatToNumberTypeProps(trail, ['saturationModifier', 'dryTimeModifier', 'precipitationLookbackDays']),
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const trailRaw = await flowfeedApiQueryService.patch<TrailDTO>(flowfeedApiQueryRequest)
      return TrailService.formatTrail(trailRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async delete(id: number): Promise<void> {
    const flowfeedApiQueryRequest = {
      endpoint: `trail/${id}`,
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      await flowfeedApiQueryService.delete(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async import(
    trails: (Partial<Trail> & {
      systemId?: number
      weatherGroupId?: number | null
      displayWeatherGroupId?: number | null
      featureIds?: number[]
      conditionalStatusGroupIds?: number[]
      ways?: TrailWay[]
    })[],
    options?: {
      matchingField?: string | undefined
      deleteUnmatchedExisting?: boolean
    },
  ): Promise<void> {
    const data = trails.map((trail) => {
      const numberTypeProps = [
        'id',
        'systemId',
        'weatherGroupId',
        'displayWeatherGroupId',
        'distance',
        'descent',
        'climb',
        'gpsLat',
        'gpsLong',
        'parkingGpsLat',
        'parkingGpsLong',
        'weatherResistance',
        'saturationModifier',
        'freezeModifier',
        'thawModifier',
        'dryTimeModifier',
        'sortWeight',
        'precipitationLookbackDays',
      ]

      // Parse ways from string to array if it comes as a JSON string from CSV
      const parsedTrail = { ...trail }
      if (typeof parsedTrail.ways === 'string') {
        try {
          parsedTrail.ways = JSON.parse(parsedTrail.ways)
        } catch {
          parsedTrail.ways = []
        }
      }

      // Parse precipitationLookbackDays to handle null values and string conversions
      if (typeof parsedTrail.precipitationLookbackDays === 'string') {
        if (
          parsedTrail.precipitationLookbackDays === 'null' ||
          parsedTrail.precipitationLookbackDays === 'NULL' ||
          parsedTrail.precipitationLookbackDays === ''
        ) {
          parsedTrail.precipitationLookbackDays = null
        } else {
          const parsed = Number(parsedTrail.precipitationLookbackDays)
          parsedTrail.precipitationLookbackDays = isNaN(parsed) ? null : parsed
        }
      }

      // Parse other numeric fields that might be empty strings
      const numericFields = ['climb', 'descent', 'gpsLat', 'gpsLong', 'parkingGpsLat', 'parkingGpsLong']
      numericFields.forEach((field) => {
        const fieldKey = field as keyof typeof parsedTrail
        if (
          typeof parsedTrail[fieldKey] === 'string' &&
          (parsedTrail[fieldKey] === '' || parsedTrail[fieldKey] === 'null' || parsedTrail[fieldKey] === 'NULL')
        ) {
          ;(parsedTrail as any)[fieldKey] = null
        }
      })

      // Validate weatherResistance is within valid range (1-21)
      if (typeof parsedTrail.weatherResistance === 'number') {
        if (parsedTrail.weatherResistance < 1 || parsedTrail.weatherResistance > 21) {
          console.warn(
            `Invalid weatherResistance: ${parsedTrail.weatherResistance}, must be between 1-21, setting to default 5`,
          )
          parsedTrail.weatherResistance = 5 as any
        }
      }

      // Parse difficultyId to handle string validation for Difficulties enum
      if (typeof parsedTrail.difficultyId === 'string') {
        // The Difficulties enum uses string values like '1', '2', '3', etc.
        // So we keep it as a string but validate it's a valid enum value
        const validDifficultyIds = ['1', '2', '3', '4', '5']
        if (!validDifficultyIds.includes(parsedTrail.difficultyId)) {
          // If it's not a valid string enum value, try to convert from number
          const parsed = Number(parsedTrail.difficultyId)
          if (!isNaN(parsed) && parsed >= 1 && parsed <= 5) {
            parsedTrail.difficultyId = parsed.toString() as any
          } else {
            // If we can't convert to a valid difficulty, log a warning and set a default
            console.warn(`Invalid difficultyId: ${parsedTrail.difficultyId}, setting to default '3' (Blue)`)
            parsedTrail.difficultyId = '3' as any
          }
        }
      }

      return formatToNumberTypeProps(
        {
          ...parsedTrail,
          weatherGroupId: parsedTrail.weatherGroupId !== undefined ? parsedTrail.weatherGroupId || null : undefined,
          displayWeatherGroupId:
            parsedTrail.displayWeatherGroupId !== undefined ? parsedTrail.displayWeatherGroupId || null : undefined,
          active:
            parsedTrail.active === undefined
              ? undefined
              : typeof parsedTrail.active === 'boolean'
              ? parsedTrail.active
              : String(parsedTrail.active).toLowerCase() === 'true',
        },
        numberTypeProps,
      )
    })
    const flowfeedApiQueryRequest = {
      endpoint: `trail/sync`,
      data: {
        data,
        matchingProp: options?.matchingField,
        deleteMissing: options?.deleteUnmatchedExisting,
      },
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      await flowfeedApiQueryService.post(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async listSurfaceTypes(): Promise<string[]> {
    const flowfeedApiQueryRequest = {
      endpoint: `trail/surface-types`,
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const surfaceTypes = await flowfeedApiQueryService.get<string[]>(flowfeedApiQueryRequest)
      return surfaceTypes
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }

  public static formatTrail(trail: TrailDTO): Trail {
    return {
      id: trail.id,
      region: trail.region,
      name: trail.name,
      system: trail.system ? TrailSystemService.formatTrailSystem(trail.system) : undefined,
      weatherGroups: trail.weatherGroups
        ? trail.weatherGroups.map((item) => ({
            ...WeatherGroupService.formatWeatherGroup(item),
            priority: item.priority,
          }))
        : [],
      displayWeatherGroup: trail.displayWeatherGroup
        ? WeatherGroupService.formatWeatherGroup(trail.displayWeatherGroup)
        : undefined,
      conditionalStatusGroups: Array.isArray(trail.conditionalStatusGroups)
        ? trail.conditionalStatusGroups.map(ConditionalStatusGroupService.formatConditionalStatusGroup)
        : undefined,
      manualTrailStatuses: Array.isArray(trail.manualTrailStatuses)
        ? trail.manualTrailStatuses.map(ManualTrailStatusService.formatManualTrailStatus)
        : undefined,
      sponsor: trail.sponsor ? SponsorService.formatSponsor(trail.sponsor) : undefined,
      features: Array.isArray(trail.features) ? trail.features.map(TrailFeatureService.formatTrailFeature) : undefined,
      status: trail.status ? TrailStatusService.formatTrailStatus(trail.status) : undefined,
      difficultyId: trail.difficultyId,
      description: trail.description,
      featuredImgUrl: trail.featuredImgUrl,
      surfaceType: trail.surfaceType,
      distance: trail.distance,
      descent: trail.descent,
      climb: trail.climb,
      gpsLat: trail.gpsLat,
      gpsLong: trail.gpsLong,
      parkingGpsLat: trail.parkingGpsLat,
      parkingGpsLong: trail.parkingGpsLong,
      parkingLotId: trail.parkingLotId ?? trail.parkingLot?.id ?? null,
      parkingLot: trail.parkingLot
        ? {
            ...trail.parkingLot,
            createdAt: new Date(trail.parkingLot.createdAt),
            updatedAt: new Date(trail.parkingLot.updatedAt),
          }
        : null,
      street: trail.street,
      city: trail.city,
      state: trail.state,
      weatherResistance: trail.weatherResistance,
      saturationModifier: trail.saturationModifier,
      freezeModifier: trail.freezeModifier,
      thawModifier: trail.thawModifier,
      dryTimeModifier: trail.dryTimeModifier,
      sortWeight: trail.sortWeight,
      permalink: trail.permalink,
      keywords: trail.keywords,
      active: trail.active,
      ways: trail.ways ? trail.ways.map((way) => ({ wayId: way.wayId })) : [],
      osmRelations: trail.osmRelations
        ? trail.osmRelations.map((relation) => ({ relationId: relation.relationId }))
        : [],
      // Preserve undefined when the relation wasn't loaded so callers can distinguish
      // "not requested" from "loaded but empty" — the chip and CSV export use this to
      // avoid rendering a false-positive "no TF mapping" on every trail in the search grid.
      trailforksIds: trail.trailforksIds
        ? trail.trailforksIds.map((link) => ({ trailforksTrailId: link.trailforksTrailId }))
        : undefined,
      updatedAt: new Date(trail.updatedAt),
      createdAt: new Date(trail.createdAt),
      precipitationLookbackDays: trail.precipitationLookbackDays,
      freezeThawEnabled: trail.freezeThawEnabled,
      trailforksSyncEnabled: trail.trailforksSyncEnabled,
    }
  }
}
